import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import mk from "date-fns/locale/mk";

import { Container, Row, Col, Label, Input } from "reactstrap";
import { useSelector } from "react-redux";

import { formatDateManager } from "managers/formatDateManager";
import { staticData } from "managers/staticDataManager";

import { loadPublicDocuments, loadIssuers, loadLayouts } from "views/callApi";

const PublicFilter = (params) => {
  const history = useHistory();
  const state = useSelector((state) => state);
  const { channel } = useSelector((state) => state.staticData);
  const { language } = useSelector((state) => state.account);

  const [filterData, setFilterData] = useState(null);
  const [issuersData, setIssuersData] = useState([]);
  const [layoutsData, setLayoutsData] = useState([]);
  const [timer, setTimer] = useState(null);
  const [keyWord, setKeyWord] = useState("");

  const [activeFilterData, setActiveFilterData] = useState(null);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [activeLanguageId, setActiveLanguageId] = useState(null);

  const allIssuers = staticData.loadTerms(useSelector, "VW_FILTER_ALLISSUERS");
  const allLayouts = staticData.loadTerms(useSelector, "VW_FILTER_ALLLAYOUTS");

  const hasOwnValue = (obj, key) => {
    return obj && Object.prototype.hasOwnProperty.call(obj, key);
  };

  const updateParentData = (newData) => {
    if (typeof params.setData === "function") {
      params.setData(newData);
    }
  };

  const areFiltersEqual = (first, second) => {
    if (!first || !second) return false;

    return (
      first.keyWord === second.keyWord &&
      first.issuerId === second.issuerId &&
      first.languageId === second.languageId &&
      first.channelId === second.channelId &&
      first.layoutCode === second.layoutCode &&
      first.layoutCodes === second.layoutCodes &&
      first.layoutId === second.layoutId &&
      first.dateFrom === second.dateFrom &&
      first.dateTo === second.dateTo &&
      first.page === second.page &&
      first.sortField === second.sortField &&
      first.sortOrder === second.sortOrder
    );
  };

  const syncParentData = (newData) => {
    if (!areFiltersEqual(params.data, newData)) {
      updateParentData(newData);
    }
  };

  const getUrlLangSegment = () =>
    (history.location.pathname.split("/")[1] || "").toLowerCase();

  const langToSegment = (code) => (code || "").split("-")[0].toLowerCase();

  const currentLangSegment =
    getUrlLangSegment() || langToSegment(language?.code) || "mk";

  const withLang = (path) => {
    if (!path || typeof path !== "string") return path;
    if (/^https?:\/\//i.test(path)) return path;

    const p = path.startsWith("/") ? path : `/${path}`;

    if (/^\/[a-z]{2}(?=\/|$)/i.test(p)) return p;

    if (p === "/") return `/${currentLangSegment}`;
    return `/${currentLangSegment}${p}`;
  };

  React.useEffect(() => {
    const filter = prepareFilter(params.data);

    setFilterData(filter);
    setKeyWord(filter?.keyWord ?? "");
    syncParentData(filter);

    if (!channel?.channelId) return;
    if (!language?.languageId) return;

    if (activeChannelId && activeChannelId === channel.channelId) {
      return;
    } else {
      setActiveChannelId(channel.channelId);
    }

    loadIssuers(
      state,
      channel.channelId,
      handleIssuersData,
      params.setIsBusy,
      params.alertRef
    );

    if (activeLanguageId && activeLanguageId === language.languageId) {
      return;
    } else {
      setActiveLanguageId(language.languageId);
    }

    loadLayouts(
      state,
      channel.channelId,
      language.languageId,
      handleLayoutsData,
      params.setIsBusy,
      params.alertRef
    );
  }, [
    params.data,
    history.location.pathname,
    channel?.channelId,
    language?.languageId,
  ]);

  React.useEffect(() => {
    const filter = prepareFilter(params.data);

    setFilterData(filter);
    setKeyWord(filter?.keyWord ?? "");
    syncParentData(filter);
  }, [layoutsData]);

  React.useEffect(() => {
    if (filterData?.isPushRequest && filterData?.layoutCode) {
      history.push(
        withLang(`/documents/${filterData.layoutCode}`),
        filterData
      );
      return;
    }

    if (filterData?.isPushRequest && filterData?.issuerId) {
      history.push(
        withLang(`/search/${filterData.issuerId}`),
        filterData
      );
      return;
    }

    if (filterData?.isPushRequest) {
      history.push(withLang("/documents/"), filterData);
      return;
    }

    if (!filterChanges()) {
      return;
    }

    setActiveFilterData(filterData);
    syncParentData(filterData);

    if (!filterData || !filterData.channelId) {
      return;
    }

    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
      state: filterData,
    });

    loadPublicDocuments(
      state,
      params.setDocumentsData,
      filterData,
      params.setIsBusy,
      params.alertRef
    );
  }, [filterData]);

  React.useEffect(() => {
    if (
      !filterData ||
      !state?.account?.language?.languageId ||
      filterData.languageId === state?.account?.language?.languageId
    ) {
      return;
    }

    const newFilterData = {
      ...filterData,
      languageId: state?.account?.language?.languageId,
    };

    updateParentData(newFilterData);
    setFilterData(newFilterData);
  }, [state?.account?.language?.languageId]);

  const handleIssuersData = (data) => {
    if (!data) {
      setIssuersData([]);
      return;
    }

    data.unshift({ issuerId: 0 });
    setIssuersData(data);
  };

  const handleLayoutsData = (data) => {
    if (!data) {
      setLayoutsData([]);
      return;
    }

    data.unshift({ layoutId: 0 });
    setLayoutsData(data);
  };

  const prepareFilter = (data) => {
    const locationStateFilter = history?.location?.state;

    const selectedIssuerId = hasOwnValue(data, "issuerId")
      ? data.issuerId
      : hasOwnValue(locationStateFilter, "issuerId")
      ? locationStateFilter.issuerId
      : filterData?.issuerId;

    const selectedLayoutCode = hasOwnValue(data, "layoutCode")
      ? data.layoutCode
      : hasOwnValue(data, "layoutCodes")
      ? data.layoutCodes
      : hasOwnValue(locationStateFilter, "layoutCode")
      ? locationStateFilter.layoutCode
      : hasOwnValue(locationStateFilter, "layoutCodes")
      ? locationStateFilter.layoutCodes
      : params?.match?.params?.layouts;

    const selectedLayoutId = hasOwnValue(data, "layoutId")
      ? data.layoutId
      : resolveLayoutIdByLayoutCode(selectedLayoutCode) ??
        locationStateFilter?.layoutId;

    const selectedDateFrom = hasOwnValue(data, "dateFrom")
      ? data.dateFrom
      : formatDateManager.formatISOWithStartHour(
          handleDateFrom(locationStateFilter?.dateFrom ?? filterData?.dateFrom)
        );

    const selectedDateTo = hasOwnValue(data, "dateTo")
      ? data.dateTo
      : formatDateManager.formatISOWithEndHour(
          handleDateTo(locationStateFilter?.dateTo ?? filterData?.dateTo)
        );

    const result = {
      keyWord: hasOwnValue(data, "keyWord")
        ? data.keyWord
        : locationStateFilter?.keyWord ?? filterData?.keyWord,

      issuerId: selectedIssuerId,

      languageId: state?.account?.language?.languageId,

      channelId: channel?.channelId,

      layoutCode: selectedLayoutCode,

      layoutCodes: selectedLayoutCode,

      layoutId: selectedLayoutId,

      dateFrom: selectedDateFrom,

      dateTo: selectedDateTo,

      page: hasOwnValue(data, "page")
        ? data.page
        : locationStateFilter?.page ?? 1,

      isPushRequest: false,

      sortField: hasOwnValue(data, "sortField")
        ? data.sortField
        : locationStateFilter?.sortField,

      sortOrder: hasOwnValue(data, "sortOrder")
        ? data.sortOrder
        : locationStateFilter?.sortOrder,
    };

    return result;
  };

  const filterChanges = () => {
    return !areFiltersEqual(activeFilterData, filterData);
  };

  const resolveLayoutIdByLayoutCode = (layoutCode) => {
    if (layoutCode) {
      const resolve = layoutsData.find((ld) => ld.layoutCode === layoutCode);
      return resolve?.layoutId;
    }

    return null;
  };

  const resolveLayoutCode = (layoutId) => {
    const resolve = layoutsData.find((ld) => ld.layoutId === layoutId);
    return resolve?.layoutCode;
  };

  const handleDateFrom = (data) => {
    if (data) {
      try {
        return new Date(data);
      } catch (e) {
        // console.log("handleDateFrom-error: ", e);
      }
    }

    const result = new Date();
    result.setMonth(result.getMonth() - 6);
    return result;
  };

  const handleDateTo = (data) => {
    if (data) {
      try {
        return new Date(data);
      } catch (e) {
        // console.log("handleDateTo-error: ", e);
      }
    }

    return new Date();
  };

  const getIssuerDisplayName = (issuer) => {
    if (issuer?.localizedTerms == null) {
      return issuer?.code ? issuer.code : allIssuers;
    }

    const result = issuer?.localizedTerms?.find(
      (l) => l.languageId === state.account.language.languageId
    );

    return result?.displayName ?? issuer.code;
  };

  const getIssuers = () => {
    return issuersData
      .map((issuer) => ({
        ...issuer,
        displayName: getIssuerDisplayName(issuer),
      }))
      .sort((a, b) => {
        if (a.issuerId === 0) return -1;
        if (b.issuerId === 0) return 1;
        return a.displayName.localeCompare(b.displayName);
      });
  };

  const getLayoutDisplayName = (layout) => {
    if (layout?.description) return layout.description;
    return layout?.code ? layout.code : allLayouts;
  };

  const handleKeyWord = (e) => {
    setKeyWord(e.target.value);
  
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  
    const timerSetter = setTimeout(() => {
      const newFilterData = {
        ...filterData,
        page: 1,
        keyWord: e.target.value,
        isPushRequest: true,
      };
  
      updateParentData(newFilterData);
      setFilterData(newFilterData);
  
      setTimer(null);
    }, 7000);
  
    setTimer(timerSetter);
  };
  
  return (
    <Container>
      <Row className="mt-3">
        <Col
          sm="6"
          md="6"
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          <Label
            key="labelKeyWord"
            id="labelKeyWord"
            for="formKeyWord"
            className="mr-1"
          >
            {staticData.loadTerms(useSelector, "VW_KEYWORD")}
          </Label>
          <Input
            key="formKeyWord"
            id="formKeyWord"
            name="keyWord"
            type="text"
            disabled={params.isBusy}
            className="form-control form-control-sm"
            value={keyWord}
            onChange={handleKeyWord}
          />
        </Col>

        <Col
          sm="3"
          md="3"
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          <Label
            key="labelDateFrom"
            id="labelDateFrom"
            for="formDateFrom"
            className="mr-1"
          >
            {staticData.loadTerms(useSelector, "VW_FROM")}
          </Label>
          <DatePicker
            key="formDateFrom"
            id="formDateFrom"
            name="dateFrom"
            disabled={params.isBusy}
            className="form-control form-control-sm"
            dateFormat="dd.MM.yyyy"
            selected={handleDateFrom(filterData?.dateFrom)}
            onSelect={(date) => {
              const newFilterData = {
                ...filterData,
                page: 1,
                dateFrom: formatDateManager.formatISOWithStartHour(
                  new Date(date)
                ),
                isPushRequest: false,
              };

              updateParentData(newFilterData);
              setFilterData(newFilterData);
            }}
            locale={mk}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
        </Col>

        <Col
          sm="3"
          md="3"
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          <Label
            key="labelDateTo"
            id="labelDateTo"
            for="formDateTo"
            className="mr-1"
          >
            {staticData.loadTerms(useSelector, "VW_TO")}
          </Label>
          <DatePicker
            key="formDateTo"
            id="formDateTo"
            name="dateTo"
            disabled={params.isBusy}
            className="form-control form-control-sm"
            dateFormat="dd.MM.yyyy"
            selected={handleDateTo(filterData?.dateTo)}
            onSelect={(date) => {
              const newFilterData = {
                ...filterData,
                page: 1,
                dateTo: formatDateManager.formatISOWithEndHour(new Date(date)),
                isPushRequest: false,
              };

              updateParentData(newFilterData);
              setFilterData(newFilterData);
            }}
            locale={mk}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
        </Col>
      </Row>

      <Row>
        <Col
          sm="6"
          md="6"
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          <Label
            key="labelIssuerId"
            id="labelIssuerId"
            for="formIssuerId"
            className="mr-1"
          >
            {staticData.loadTerms(useSelector, "VW_COMMON_ISSUER")}
          </Label>
          <Input
            key="formIssuerId"
            id="formIssuerId"
            name="issuerId"
            type="select"
            disabled={params.isBusy}
            className="form-control form-control-sm"
            value={filterData?.issuerId ?? 0}
            onChange={(e) => {
              const tmpIssuerId =
                e.target.value === "0" ? undefined : parseInt(e.target.value);

              const newFilterData = {
                ...filterData,
                page: 1,
                issuerId: tmpIssuerId,
                isPushRequest: true,
              };

              updateParentData(newFilterData);
              setFilterData(newFilterData);
            }}
          >
            {getIssuers().map((issuer) => (
              <option
                key={issuer?.issuerId ? issuer.issuerId : 0}
                data-key={issuer?.issuerId}
                value={issuer?.issuerId}
              >
                {issuer?.displayName}
              </option>
            ))}
          </Input>
        </Col>

        <Col
          sm="6"
          md="6"
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          <Label
            key="labelLayoutId"
            id="labelLayoutId"
            for="formLayoutId"
            className="mr-1"
          >
            {staticData.loadTerms(useSelector, "VW_COMMON_LAYOUT")}
          </Label>
          <Input
            key="formLayoutId"
            id="formLayoutId"
            name="layoutId"
            type="select"
            disabled={params.isBusy}
            className="form-control form-control-sm"
            value={filterData?.layoutId ?? 0}
            onChange={(e) => {
              const tmpLayoutId =
                e.target.value === "0" ? undefined : parseInt(e.target.value);
              const tmpLayoutCode = resolveLayoutCode(tmpLayoutId);

              const newFilterData = {
                ...filterData,
                page: 1,
                layoutId: tmpLayoutId,
                layoutCode: tmpLayoutCode,
                layoutCodes: tmpLayoutCode,
                isPushRequest: true,
              };

              updateParentData(newFilterData);
              setFilterData(newFilterData);
            }}
          >
            {layoutsData.map((layout) => (
              <option
                key={layout?.layoutId ?? 0}
                data-key={layout?.layoutId}
                value={layout?.layoutId ?? 0}
              >
                {getLayoutDisplayName(layout)}
              </option>
            ))}
          </Input>
        </Col>
      </Row>
    </Container>
  );
};

export default PublicFilter;